import { useCallback, useEffect, useMemo, useState, type FormEvent, type MouseEvent } from 'react';
import { Calculator, CheckCircle2, FileCheck2, Plus, Send, Trash2 } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import { ServicePage } from '@forq/service';
import { SearchSelect } from '@forq/ui';
import type { OrderGateway, OrderPositionKind, OrderRecord } from '@forq/orders-data';
import type { ServiceEstimateLineKind, ServiceGateway, ServiceJobRecord } from '@forq/service-data';
import type { ServiceBillingGateway, ServiceBillingLineKind, ServiceBillingProposal } from '@forq/service-billing-data';
import { emptyCompanyProfile, type CompanyProfileGateway, type TenantCompanyProfile } from '@forq/platform-data';
import { renderServiceBillingHtml, serviceDocumentLabels } from './serviceBillingDocument';

const estimateKindToBilling: Readonly<Record<ServiceEstimateLineKind, ServiceBillingLineKind>> = {
  labour: 'labour', travel: 'travel-time', part: 'article', service: 'service', text: 'text',
};

/** Baut aus dem Serviceauftrag + seinem Kostenvoranschlag die proposal-Form, die der FTW-Render erwartet. */
function estimateToProposal(job: ServiceJobRecord): ServiceBillingProposal {
  const estimate = job.estimate;
  return {
    id: job.id, tenantId: job.tenantId, serviceJobId: job.id, serviceNumber: job.serviceNumber,
    orderId: job.orderId, orderNumber: job.orderNumber, customerId: job.customerId, customerNumber: job.customerNumber,
    customerName: job.customerName, invoiceAddress: `${job.address.street}, ${job.address.postalCode} ${job.address.city}`.trim(),
    machineLabels: job.machineLabel ? [job.machineLabel] : [], serviceDate: (job.scheduledStart ?? estimate?.createdAt ?? '').slice(0, 10),
    technicianNames: job.technicianName ? [job.technicianName] : [], workPerformed: job.workDescription, findings: '',
    paperPrintDocumentId: null, customerSignatureOnPaper: false,
    lines: (estimate?.lines ?? []).map((line, index) => ({
      id: `${job.id}-est-${index}`, kind: estimateKindToBilling[line.kind], sourceNumber: '',
      description: line.description, quantity: line.quantity, unit: line.unit, unitPrice: line.unitPrice,
      discountPercent: line.discountPercent, vatRate: line.vatRate, netAmount: line.netAmount,
    })),
    netTotal: estimate?.netTotal ?? 0, vatTotal: estimate?.vatTotal ?? 0, grossTotal: estimate?.grossTotal ?? 0,
    status: 'draft', version: 1, preparedBy: '', preparedAt: estimate?.createdAt ?? '',
    returnedReason: '', returnedAt: null, invoicedNumber: '',
  };
}

interface Props {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly serviceGateway: ServiceGateway;
  readonly ordersGateway: OrderGateway;
  readonly billingGateway: ServiceBillingGateway;
  readonly companyGateway: CompanyProfileGateway;
  readonly preparedBy: string;
}

interface BillingLineDraft {
  readonly id: string;
  kind: ServiceBillingLineKind;
  sourceNumber: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discountPercent: number;
  vatRate: number;
}

function newLine(kind: ServiceBillingLineKind = 'labour'): BillingLineDraft {
  return {
    id: crypto.randomUUID(),
    kind,
    sourceNumber: '',
    description: kind === 'labour' ? 'Servicetechniker' : kind === 'travel-time' ? 'Fahrtzeit' : kind === 'kilometres' ? 'Fahrzeugkosten / Kilometer' : '',
    quantity: 1,
    unit: kind === 'kilometres' ? 'km' : kind === 'article' ? 'Stück' : 'Std.',
    unitPrice: 0,
    discountPercent: 0,
    vatRate: 19,
  };
}

function billingKind(kind: OrderPositionKind): ServiceBillingLineKind {
  if (kind === 'labor') return 'labour';
  if (kind === 'travel') return 'travel-time';
  if (kind === 'article') return 'article';
  if (kind === 'service') return 'service';
  return 'text';
}

function linesFromOrder(order: OrderRecord): BillingLineDraft[] {
  return order.positions
    .filter((position) => position.description.trim() || position.unitPrice > 0)
    .map((position) => ({
      id: crypto.randomUUID(),
      kind: billingKind(position.kind),
      sourceNumber: position.sourceNumber,
      description: position.description,
      quantity: position.quantity || 1,
      unit: position.unit || 'Stück',
      unitPrice: position.unitPrice,
      discountPercent: position.discountPercent,
      vatRate: position.vatRate,
    }));
}

function linesFromPaper(job: ServiceJobRecord): BillingLineDraft[] {
  const paper = job.paperReturn;
  if (!paper) return [newLine('labour')];
  const result: BillingLineDraft[] = [];
  if (paper.workHours > 0) result.push({ ...newLine('labour'), quantity: paper.workHours });
  if (paper.travelHours > 0) result.push({ ...newLine('travel-time'), quantity: paper.travelHours });
  if (paper.kilometers > 0) result.push({ ...newLine('kilometres'), quantity: paper.kilometers });
  paper.materials.forEach((material) => result.push({
    ...newLine('article'),
    sourceNumber: material.articleNumber,
    description: material.description,
    quantity: material.quantity,
    unit: material.unit,
  }));
  return result.length > 0 ? result : [newLine('labour')];
}

/** Rechnungspositionen aus dem Kostenvoranschlag -- so setzt die Abrechnung auf dem KV auf. */
function linesFromEstimate(job: ServiceJobRecord): BillingLineDraft[] {
  return (job.estimate?.lines ?? []).map((line) => ({
    id: crypto.randomUUID(),
    kind: estimateKindToBilling[line.kind],
    sourceNumber: '',
    description: line.description,
    quantity: line.quantity,
    unit: line.unit,
    unitPrice: line.unitPrice,
    discountPercent: line.discountPercent,
    vatRate: line.vatRate,
  }));
}

function money(value: number) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

export function ServiceBillingWorkspace(props: Props) {
  const [selected, setSelected] = useState<ServiceJobRecord | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [lines, setLines] = useState<BillingLineDraft[]>([newLine('labour')]);
  const [serviceDate, setServiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [workPerformed, setWorkPerformed] = useState('');
  const [findings, setFindings] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [company, setCompany] = useState<TenantCompanyProfile>(emptyCompanyProfile);
  const [estimatePrint, setEstimatePrint] = useState<ServiceJobRecord | null>(null);

  useEffect(() => {
    let active = true;
    props.companyGateway.loadProfile(props.tenant.id).then((profile) => { if (active) setCompany(profile); }).catch(() => {});
    return () => { active = false; };
  }, [props.companyGateway, props.tenant.id]);

  const openCompletion = useCallback(async (job: ServiceJobRecord) => {
    setSelected(job);
    setBusy(true);
    try {
      const order = await props.ordersGateway.getOrder(props.tenant.id, job.orderId);
      // Vorrang: der Kostenvoranschlag (die Rechnung setzt darauf auf), sonst Orders-Positionen, sonst Papier.
      const estimateLines = linesFromEstimate(job);
      const orderLines = linesFromOrder(order);
      setLines(estimateLines.length > 0 ? estimateLines : orderLines.length > 0 ? orderLines : linesFromPaper(job));
      setWorkPerformed(job.paperReturn?.workPerformed || order.notes || job.workDescription);
      setFindings(job.paperReturn?.followUpNotes ?? '');
      setServiceDate((job.paperReturn?.receivedAt || job.scheduledStart || new Date().toISOString()).slice(0, 10));
      setEditorOpen(true);
      setMessage('');
    } catch (error) {
      setLines(linesFromPaper(job));
      setWorkPerformed(job.paperReturn?.workPerformed || job.workDescription);
      setFindings(job.paperReturn?.followUpNotes ?? '');
      setServiceDate((job.paperReturn?.receivedAt || job.scheduledStart || new Date().toISOString()).slice(0, 10));
      setEditorOpen(true);
      setMessage(error instanceof Error ? `Orders-Positionen konnten nicht vorgeladen werden: ${error.message}` : 'Orders-Positionen konnten nicht vorgeladen werden.');
    } finally {
      setBusy(false);
    }
  }, [props.ordersGateway, props.tenant.id]);

  const resolveRowJob = useCallback(async (row: HTMLButtonElement): Promise<ServiceJobRecord | null> => {
    const id = row.getAttribute('data-service-job-id');
    if (id) return props.serviceGateway.getJob(props.tenant.id, id);
    const serviceNumber = row.querySelector<HTMLElement>(':scope > span:first-child strong')?.textContent?.trim() ?? '';
    if (!serviceNumber) return null;
    const result = await props.serviceGateway.searchJobs({ tenantId: props.tenant.id, bucket: 'all', query: serviceNumber, pageSize: 20 });
    return result.items.find((job) => job.serviceNumber === serviceNumber) ?? result.items[0] ?? null;
  }, [props.serviceGateway, props.tenant.id]);

  const captureSelection = useCallback(async (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const billingAction = target.closest<HTMLButtonElement>('.service-next-action');
    if (billingAction?.textContent?.includes('Zur Abrechnung freigeben')) {
      event.preventDefault();
      event.stopPropagation();
      if (selected) await openCompletion(selected);
      return;
    }
    const row = target.closest<HTMLButtonElement>('.service-table__row, .erp-table__row');
    if (!row) return;
    try {
      const job = await resolveRowJob(row);
      if (job) setSelected(job);
    } catch {
      setSelected(null);
    }
  }, [openCompletion, resolveRowJob, selected]);

  const patchLine = (id: string, patch: Partial<BillingLineDraft>) => setLines((current) => current.map((line) => line.id === id ? { ...line, ...patch } : line));
  const net = lines.reduce((sum, line) => sum + line.quantity * line.unitPrice * (1 - line.discountPercent / 100), 0);
  const vat = lines.reduce((sum, line) => sum + line.quantity * line.unitPrice * (1 - line.discountPercent / 100) * line.vatRate / 100, 0);

  const submit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;
    setBusy(true);
    try {
      await props.billingGateway.saveAndSubmit({
        tenantId: props.tenant.id,
        serviceJobId: selected.id,
        serviceNumber: selected.serviceNumber,
        orderId: selected.orderId,
        orderNumber: selected.orderNumber,
        customerId: selected.customerId,
        customerNumber: selected.customerNumber,
        customerName: selected.customerName,
        invoiceAddress: `${selected.address.street}, ${selected.address.postalCode} ${selected.address.city}`,
        machineLabels: [selected.machineLabel || 'Gerät nicht bekannt'],
        serviceDate,
        technicianNames: [selected.paperReturn?.technicianName || selected.technicianName || 'nicht zugewiesen'],
        workPerformed,
        findings,
        paperPrintDocumentId: selected.paperReturn?.printDocumentId ?? selected.latestPrint?.id ?? null,
        customerSignatureOnPaper: selected.paperReturn?.customerSignatureOnPaper ?? false,
        preparedBy: props.preparedBy,
        lines: lines.map(({ id: _id, ...line }) => line),
      });
      let nextJob = selected;
      if (!['billing-ready', 'completed', 'cancelled'].includes(selected.status)) {
        nextJob = await props.serviceGateway.transitionJob({
          tenantId: props.tenant.id,
          serviceJobId: selected.id,
          toStatus: 'billing-ready',
          reason: 'Rechnungsvorschlag durch Service erzeugt und an FORQ Finance übergeben.',
        });
      }
      setSelected(nextJob);
      setEditorOpen(false);
      setMessage(`Rechnungsvorschlag für ${selected.serviceNumber} wurde an FORQ Finance übertragen.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Der Rechnungsvorschlag konnte nicht übertragen werden.');
    } finally {
      setBusy(false);
    }
  }, [findings, lines, props.billingGateway, props.preparedBy, props.serviceGateway, props.tenant.id, selected, serviceDate, workPerformed]);

  const directAvailable = selected && !['billing-ready', 'completed', 'cancelled'].includes(selected.status);

  return (
    <div className="service-billing-workspace" onClickCapture={(event) => void captureSelection(event)}>
      <ServicePage tenant={props.tenant} mode={props.mode} permissions={props.permissions} enabledFeatureIds={props.enabledFeatureIds} gateway={props.serviceGateway} onPrintEstimate={setEstimatePrint} />

      {selected ? (
        <section className="service-completion-panel">
          <header>
            <div>
              <span>Serviceabschluss</span>
              <h2>{selected.serviceNumber} · {selected.paperReturn ? 'Auftrag fertig machen' : 'Direkte Abrechnung'}</h2>
              <p>{selected.paperReturn ? 'Papier-Rückmeldung prüfen, Positionen festlegen und an Finance übergeben.' : 'Für nachträglich gemeldete Einsätze ohne vorherige Planung direkt einen Rechnungsvorschlag aus den Vorgangsdaten erzeugen.'}</p>
            </div>
            {directAvailable ? <button type="button" className="is-primary" disabled={busy} onClick={() => void openCompletion(selected)}><Calculator size={15} /> {selected.paperReturn ? 'Auftrag fertig machen' : 'Direkt zur Rechnungsprüfung'}</button> : null}
          </header>
          <div className="service-completion-checks">
            <span><CheckCircle2 size={14} /> Kunde und Maschine aus Vorgang</span>
            <span><CheckCircle2 size={14} /> Positionen aus Orders übernehmbar</span>
            <span><CheckCircle2 size={14} /> Papier {selected.paperReturn ? 'zurückgegeben' : 'nicht erforderlich'}</span>
            <span><CheckCircle2 size={14} /> Status {selected.status === 'billing-ready' ? 'an Finance übergeben' : 'noch nicht übergeben'}</span>
          </div>
          {message ? <div className="service-completion-message"><FileCheck2 size={15} /> {message}</div> : null}
        </section>
      ) : null}

      {editorOpen && selected ? (
        <div className="service-completion-editor" role="dialog" aria-modal="true">
          <form onSubmit={(event) => void submit(event)}>
            <header><div><span>Rechnungsvorschlag Service</span><h2>{selected.serviceNumber} · {selected.customerName}</h2><p>{selected.paperReturn ? 'Angaben des zurückgegebenen Papierauftrags und Vorgangspositionen prüfen.' : 'Direktabrechnung ohne vorherige Planung oder Papierausgabe.'}</p></div><button type="button" onClick={() => setEditorOpen(false)}>Schließen</button></header>
            <div className="service-completion-grid">
              <label>Leistungsdatum<input type="date" value={serviceDate} onChange={(event) => setServiceDate(event.target.value)} required /></label>
              <label className="is-wide">Ausgeführte Arbeiten<textarea rows={4} value={workPerformed} onChange={(event) => setWorkPerformed(event.target.value)} required /></label>
              <label className="is-wide">Weitere Befunde / Empfehlungen<textarea rows={3} value={findings} onChange={(event) => setFindings(event.target.value)} /></label>
            </div>
            <section className="service-billing-lines"><header><h3>Abrechnungspositionen</h3><button type="button" onClick={() => setLines((current) => [...current, newLine('article')])}><Plus size={14} /> Position</button></header>
              <div className="service-billing-lines__head"><span>Art</span><span>Nr.</span><span>Bezeichnung</span><span>Menge</span><span>Einheit</span><span>Preis</span><span>Rabatt</span><span>Netto</span><span /></div>
              {lines.map((line) => <div className="service-billing-lines__row" key={line.id}>
                <SearchSelect value={line.kind} onChange={(next) => patchLine(line.id, { kind: next as ServiceBillingLineKind })} ariaLabel="Positionsart" options={[{ value: 'labour', label: 'Arbeitszeit' }, { value: 'travel-time', label: 'Fahrtzeit' }, { value: 'kilometres', label: 'Kilometer' }, { value: 'article', label: 'Ersatzteil' }, { value: 'service', label: 'Dienstleistung' }, { value: 'text', label: 'Freitext' }]} />
                <input value={line.sourceNumber} onChange={(event) => patchLine(line.id, { sourceNumber: event.target.value })} />
                <input value={line.description} onChange={(event) => patchLine(line.id, { description: event.target.value })} required />
                <input type="number" step="0.01" min="0" value={line.quantity} onChange={(event) => patchLine(line.id, { quantity: Number(event.target.value) })} />
                <input value={line.unit} onChange={(event) => patchLine(line.id, { unit: event.target.value })} />
                <input type="number" step="0.01" min="0" value={line.unitPrice} onChange={(event) => patchLine(line.id, { unitPrice: Number(event.target.value) })} />
                <input type="number" step="0.1" min="0" max="100" value={line.discountPercent} onChange={(event) => patchLine(line.id, { discountPercent: Number(event.target.value) })} />
                <strong>{money(line.quantity * line.unitPrice * (1 - line.discountPercent / 100))}</strong>
                <button type="button" onClick={() => setLines((current) => current.filter((item) => item.id !== line.id))}><Trash2 size={14} /></button>
              </div>)}
            </section>
            <footer><div><span>Netto <strong>{money(net)}</strong></span><span>USt. <strong>{money(vat)}</strong></span><span>Brutto <strong>{money(net + vat)}</strong></span></div><button type="submit" className="is-primary" disabled={busy}><Send size={15} /> {busy ? 'Überträgt …' : 'An FORQ Finance übergeben'}</button></footer>
          </form>
        </div>
      ) : null}

      {estimatePrint?.estimate ? <ServiceEstimateModal job={estimatePrint} company={company} onClose={() => setEstimatePrint(null)} /> : null}
    </div>
  );
}

function ServiceEstimateModal({ job, company, onClose }: {
  readonly job: ServiceJobRecord;
  readonly company: TenantCompanyProfile;
  readonly onClose: () => void;
}) {
  const html = useMemo(() => renderServiceBillingHtml({
    kind: 'estimate',
    documentNumber: job.estimate?.estimateNumber ?? '',
    proposal: estimateToProposal(job),
    company,
    issuedIso: job.estimate?.createdAt ?? '',
  }), [job, company]);
  return (
    <div className="orders-doc-modal" role="dialog" aria-modal="true" aria-label={serviceDocumentLabels.estimate} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="orders-doc-modal__panel">
        <header><span>{serviceDocumentLabels.estimate} · {job.estimate?.estimateNumber}</span><button type="button" className="orders-doc-modal__close" onClick={onClose}>Schließen</button></header>
        <iframe className="orders-doc-frame" title={serviceDocumentLabels.estimate} srcDoc={html} />
      </div>
    </div>
  );
}
